/**
 * Saanvi Crafts — personalization block logic.
 *
 * Renders no UI of its own; wires up the markup produced by blocks/sc-personalization.liquid:
 *  - mirrors visible inputs into hidden inputs owned by the product form (via the
 *    `form` attribute), so every submit path — Add to cart (AJAX), sticky bar,
 *    Buy it now, and native non-JS submit — carries the line item properties;
 *  - validates required personalization on form submit (capture phase, so it runs
 *    before the theme's product-form component) and on express-checkout clicks;
 *  - charm slot pickers, chart zoom dialog, photo preview, live name preview,
 *    progress chip.
 */
(() => {
  'use strict';

  class SCPersonalization extends HTMLElement {
    connectedCallback() {
      const cfgEl = this.querySelector('[data-sc-config]');
      this.cfg = cfgEl ? JSON.parse(cfgEl.textContent) : { required: {}, messages: {} };
      this.form = document.getElementById(this.dataset.formId) || this.closest('section')?.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');

      this.state = { name: '', charms: {}, hasPhoto: false };
      this.querySelectorAll('[data-sc-slot]').forEach((slot) => {
        this.state.charms[slot.dataset.scSlot] = null;
      });

      this.rehomeMirrors();
      this.bindName();
      this.bindFonts();
      this.bindPhoto();
      this.bindCharms();
      this.bindChart();
      this.bindValidation();
      this.syncProgress();
      this.syncExpressCheckout();
    }

    /* If the deterministic form id was not found, move the hidden mirror inputs
       physically inside whichever cart form we located, so they always submit. */
    rehomeMirrors() {
      const mirror = this.querySelector('[data-sc-mirror]');
      if (!mirror || !this.form) return;
      const idMatches = this.dataset.formId && document.getElementById(this.dataset.formId) === this.form;
      if (!idMatches) this.form.appendChild(mirror);
      // The file input must live where the user taps it, so it mirrors by reference:
      const file = this.querySelector('[data-sc-file]');
      if (file && !idMatches) this.form.appendChild(file) && this.bindPhoto();
    }

    /* ---------------- name + fonts ---------------- */
    bindName() {
      const input = this.querySelector('[data-sc-name]');
      if (!input) return;
      const count = this.querySelector('[data-sc-count]');
      const mirror = this.querySelector('[data-sc-mirror-name]') || this.form?.querySelector('[data-sc-mirror-name]');
      input.addEventListener('input', () => {
        this.state.name = input.value;
        if (mirror) mirror.value = input.value;
        if (count) count.textContent = String(input.value.length);
        this.clearError(input.closest('[data-sc-field]'));
        this.syncNamePreview();
        this.syncProgress();
      });
    }

    bindFonts() {
      const wrap = this.querySelector('[data-sc-fonts]');
      if (!wrap) return;
      const mirror = this.querySelector('[data-sc-mirror-font]') || this.form?.querySelector('[data-sc-mirror-font]');
      wrap.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sc-font]');
        if (!btn) return;
        wrap.querySelectorAll('.sc-font').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        if (mirror) mirror.value = btn.dataset.scFont;
        this.syncNamePreview();
      });
    }

    syncNamePreview() {
      const preview = this.querySelector('[data-sc-preview]');
      if (!preview) return;
      const text = this.querySelector('[data-sc-preview-text]');
      const selected = this.querySelector('.sc-font.is-selected');
      preview.hidden = this.state.name.trim().length === 0;
      if (text) {
        text.textContent = this.state.name;
        if (selected) text.style.fontFamily = getComputedStyle(selected).fontFamily;
      }
    }

    /* ---------------- photo ---------------- */
    bindPhoto() {
      const zone = this.querySelector('[data-sc-dropzone]');
      const file = this.querySelector('[data-sc-file]') || this.form?.querySelector('[data-sc-file]');
      if (!zone || !file || zone.dataset.scBound) return;
      zone.dataset.scBound = '1';

      const preview = this.querySelector('[data-sc-upload-preview]');
      const thumb = this.querySelector('[data-sc-upload-thumb]');
      const meta = this.querySelector('[data-sc-upload-meta]');
      const removeBtn = this.querySelector('[data-sc-upload-remove]');
      const field = zone.closest('[data-sc-field]');

      const accept = (f) => {
        const maxBytes = (this.cfg.maxPhotoMb || 10) * 1024 * 1024;
        if (f.size > maxBytes) {
          this.toastLikeError(field, (this.cfg.messages.photoTooBig || '').replace('{mb}', this.cfg.maxPhotoMb));
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          if (thumb) thumb.src = ev.target.result;
          if (meta) meta.textContent = `${f.name} · ${(f.size / 1048576).toFixed(1)} MB`;
          if (preview) preview.hidden = false;
          zone.hidden = true;
          this.state.hasPhoto = true;
          this.clearError(field);
          this.syncProgress();
        };
        reader.readAsDataURL(f);
      };

      zone.addEventListener('click', () => file.click());
      ['dragover', 'dragenter'].forEach((ev) => zone.addEventListener(ev, (e) => { e.preventDefault(); zone.classList.add('is-drag'); }));
      ['dragleave', 'drop'].forEach((ev) => zone.addEventListener(ev, (e) => { e.preventDefault(); zone.classList.remove('is-drag'); }));
      zone.addEventListener('drop', (e) => {
        const f = e.dataTransfer?.files?.[0];
        if (!f) return;
        const dt = new DataTransfer();
        dt.items.add(f);
        file.files = dt.files;
        accept(f);
      });
      file.addEventListener('change', () => { if (file.files[0]) accept(file.files[0]); });
      removeBtn?.addEventListener('click', () => {
        file.value = '';
        this.state.hasPhoto = false;
        if (preview) preview.hidden = true;
        zone.hidden = false;
        this.syncProgress();
        file.click();
      });
    }

    /* ---------------- charms ---------------- */
    bindCharms() {
      this.querySelectorAll('[data-sc-slot-wrap]').forEach((wrap) => {
        const slot = wrap.querySelector('[data-sc-slot]');
        const picker = wrap.querySelector('[data-sc-picker]');
        const search = wrap.querySelector('[data-sc-picker-search]');
        const label = slot.dataset.scSlot;

        slot.addEventListener('click', () => {
          const willOpen = picker.hidden;
          this.querySelectorAll('[data-sc-picker]').forEach((p) => (p.hidden = true));
          this.querySelectorAll('[data-sc-slot]').forEach((s) => s.classList.remove('is-open'));
          if (willOpen) {
            picker.hidden = false;
            slot.classList.add('is-open');
            search?.focus({ preventScroll: true });
          }
        });

        search?.addEventListener('input', () => {
          const q = search.value.toLowerCase();
          picker.querySelectorAll('.sc-charm').forEach((c) => {
            c.style.display = (c.dataset.scCharmName || '').toLowerCase().includes(q) ? '' : 'none';
          });
        });

        picker.addEventListener('click', (e) => {
          const charm = e.target.closest('[data-sc-charm]');
          if (!charm) return;
          const value = charm.dataset.scCharm; // '' means "no charm"
          const name = charm.dataset.scCharmName;
          this.state.charms[label] = value === '' ? '' : name;

          const mirror = this.querySelector(`[data-sc-mirror-charm="${CSS.escape(label)}"]`) || this.form?.querySelector(`[data-sc-mirror-charm="${CSS.escape(label)}"]`);
          if (mirror) mirror.value = value === '' ? name : name;

          picker.querySelectorAll('.sc-charm').forEach((c) => c.classList.remove('is-selected'));
          charm.classList.add('is-selected');

          const thumbEl = slot.querySelector('[data-sc-slot-thumb]');
          const img = charm.querySelector('img');
          if (thumbEl) {
            thumbEl.textContent = img ? '' : '∅';
            thumbEl.style.backgroundImage = img ? `url(${img.currentSrc || img.src})` : '';
          }
          slot.querySelector('[data-sc-slot-value]').textContent = name;
          slot.querySelector('[data-sc-slot-action]').textContent = this.cfg.messages.slotFilledCta || '✓ Change';
          slot.classList.add('is-filled');
          picker.hidden = true;
          slot.classList.remove('is-open');
          this.clearError(slot.closest('[data-sc-field]'));
          this.syncProgress();
        });
      });
    }

    /* ---------------- chart dialog ---------------- */
    bindChart() {
      const dialog = this.querySelector('[data-sc-chart-dialog]');
      if (!dialog) return;
      this.querySelectorAll('[data-sc-chart-open]').forEach((btn) => btn.addEventListener('click', () => dialog.showModal()));
      dialog.querySelector('[data-sc-chart-close]')?.addEventListener('click', () => dialog.close());
      const scroll = dialog.querySelector('[data-sc-chart-scroll]');
      const toggleZoom = () => scroll?.classList.toggle('is-zoomed');
      dialog.querySelector('[data-sc-chart-zoom]')?.addEventListener('click', toggleZoom);
      scroll?.addEventListener('click', (e) => { if (e.target.tagName === 'IMG') toggleZoom(); });
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    }

    /* ---------------- progress + validation ---------------- */
    requirements() {
      const req = [];
      if (this.cfg.required.name) {
        req.push({ ok: this.state.name.trim().length > 0, field: this.querySelector('[data-sc-field="name"]') });
      }
      if (this.cfg.required.photo) {
        req.push({ ok: this.state.hasPhoto, field: this.querySelector('[data-sc-field="photo"]') });
      }
      if (this.cfg.required.charms) {
        Object.keys(this.state.charms).forEach((label) => {
          req.push({ ok: this.state.charms[label] !== null, field: this.querySelector('[data-sc-field="charms"]') });
        });
      }
      return req;
    }

    syncProgress() {
      const chip = this.querySelector('[data-sc-progress]');
      const req = this.requirements();
      if (chip) {
        if (req.length === 0) {
          chip.hidden = true;
        } else {
          chip.hidden = false;
          const done = req.filter((r) => r.ok).length;
          const isDone = done >= req.length;
          chip.textContent = isDone
            ? this.cfg.messages.progressDone || 'All set ✓'
            : (this.cfg.messages.progress || '{done} of {total} done').replace('{done}', done).replace('{total}', req.length);
          chip.classList.toggle('is-done', isDone);
        }
      }
      this.syncExpressCheckout();
    }

    /* Shopify's dynamic checkout ("Buy it now") creates the checkout directly from
       variant + quantity and never includes line item properties, so on personalized
       products it must stay hidden permanently — completing the fields doesn't help.
       Customers check out via Add to cart, where properties flow through. */
    syncExpressCheckout() {
      if (!this.cfg.hideExpressWhenRequired || !this.form) return;
      this.form.querySelectorAll('.accelerated-checkout-block').forEach((el) => {
        el.style.display = 'none';
        if (!this.cfg.messages.expressHint || el.parentElement.querySelector('.sc-express-hint')) return;
        const hint = document.createElement('span');
        hint.className = 'sc-express-hint';
        hint.textContent = this.cfg.messages.expressHint;
        el.insertAdjacentElement('afterend', hint);
      });
    }

    validate() {
      const req = this.requirements();
      const missing = req.filter((r) => !r.ok);
      this.querySelectorAll('[data-sc-field]').forEach((f) => f.classList.remove('has-error'));
      if (missing.length === 0) return true;

      const seen = new Set();
      missing.forEach((r) => {
        if (!r.field || seen.has(r.field)) return;
        seen.add(r.field);
        r.field.classList.add('has-error', 'is-shaking');
        setTimeout(() => r.field.classList.remove('is-shaking'), 450);
      });
      const first = missing[0]?.field;
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    bindValidation() {
      if (!this.form || this.cfg.designMode) return;
      // Capture phase on the form: runs before the theme's product-form component
      // (which listens in the bubble phase on its ancestor element).
      this.form.addEventListener(
        'submit',
        (e) => {
          if (this.validate()) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();
        },
        true
      );
      // Express checkout buttons bypass form submit — intercept their clicks too.
      this.form.addEventListener(
        'click',
        (e) => {
          if (!e.target.closest('.accelerated-checkout-block, .shopify-payment-button')) return;
          if (this.validate()) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();
        },
        true
      );
    }

    clearError(field) {
      field?.classList.remove('has-error');
    }

    toastLikeError(field, message) {
      if (!field) return;
      const err = field.querySelector('[data-sc-error]');
      const original = err?.textContent;
      if (err && message) err.textContent = message;
      field.classList.add('has-error', 'is-shaking');
      setTimeout(() => {
        field.classList.remove('is-shaking');
        if (err && original && message) setTimeout(() => (err.textContent = original), 4000);
      }, 450);
    }
  }

  if (!customElements.get('sc-personalization')) {
    customElements.define('sc-personalization', SCPersonalization);
  }
})();
